import React, { useState } from 'react';
import { Phone, Printer, Mail, MapPin, Navigation } from 'lucide-react';
import Section from '../components/Section';
import IconChip from '../components/IconChip';
import SocialRow from '../components/SocialRow';
import ContactForm from './ContactForm';
import { useContactInfo, useHomeSections } from '../data';

// Trust highlights shown on the contact info side.
const STATS = [
  { num: '10+', label: 'Years experience' },
  { num: '100%', label: 'Project success' },
  { num: '24/7', label: 'Site support' },
  { num: 'Licensed', label: 'Engineering team' },
];

const clean = (s) => (s || '').replace(/[^\d+]/g, '');

// §7.1.4 — split panel: premium info side (left) + multi-step form (right) + map.
export default function ContactSection({ embedded = false }) {
  const contact = useContactInfo();
  const sections = useHomeSections();
  const [chooser, setChooser] = useState(false);

  const rawMap = (contact.mapEmbed || '').trim();
  const mapSrc = rawMap.startsWith('http')
    ? rawMap
    : `https://www.google.com/maps?q=${encodeURIComponent(rawMap || contact.formAddress || 'Israel')}&output=embed`;

  // Navigation destination (coords or address) parsed from the embed's q param.
  let dest = contact.formAddress || 'Israel';
  try {
    dest = new URL(mapSrc).searchParams.get('q') || dest;
  } catch {
    /* keep fallback */
  }
  const isCoords = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(dest);
  const gmapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
  const wazeUrl = isCoords
    ? `https://waze.com/ul?ll=${dest.replace(/\s/g, '')}&navigate=yes`
    : `https://waze.com/ul?q=${encodeURIComponent(dest)}&navigate=yes`;

  const isMobile = () =>
    typeof navigator !== 'undefined' &&
    (window.matchMedia('(pointer: coarse)').matches || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent));

  const onMapClick = () => {
    if (isMobile()) setChooser(true);
    else window.open(gmapsUrl, '_blank', 'noopener,noreferrer');
  };

  const rows = [
    { icon: Phone, text: contact.formPhone, href: `tel:${clean(contact.formPhone)}` },
    contact.fax ? { icon: Printer, text: `Fax: ${contact.fax}`, href: null } : null,
    { icon: Mail, text: contact.formEmail, href: `mailto:${contact.formEmail}` },
    { icon: MapPin, text: contact.formAddress, onClick: onMapClick },
  ].filter(Boolean);

  return (
    <Section id={embedded ? undefined : 'contact'} className={`contact${embedded ? ' contact--embedded' : ''}`}>
      <div className="contact__panel">
        <div className="contact__info">
          <p className="contact__eyebrow">Get in touch</p>
          <h2 className="contact__title">{sections.contactTitle}</h2>
          <p className="contact__para">{sections.contactBlurb}</p>

          <div className="contact__stats">
            {STATS.map((s) => (
              <div key={s.label} className="contact__stat">
                <span className="contact__stat-num">{s.num}</span>
                <span className="contact__stat-label">{s.label}</span>
              </div>
            ))}
          </div>

          <ul className="contact__rows">
            {rows.map(({ icon: Icon, text, href, onClick }) => (
              <li key={text} className="contact__row">
                <IconChip size={40} variant="accent">
                  <Icon size={18} />
                </IconChip>
                {href ? (
                  <a href={href}>{text}</a>
                ) : onClick ? (
                  <button type="button" className="contact__row-btn" onClick={onClick}>
                    {text}
                  </button>
                ) : (
                  <span>{text}</span>
                )}
              </li>
            ))}
          </ul>

          <div className="contact__reply">
            <span className="contact__reply-dot" aria-hidden="true" />
            We typically reply within 24 hours
          </div>

          <SocialRow variant="plain" order={['facebook', 'linkedin']} size={20} />
        </div>

        <div className="contact__form-wrap">
          <ContactForm />
        </div>
      </div>

      <div className="contact__map">
        <iframe
          title="Our location"
          src={mapSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
        <div className="contact__map-frame" aria-hidden="true" />
        <button className="contact__map-overlay" onClick={onMapClick} aria-label="Open navigation to our office">
          <span className="contact__map-hint">
            <Navigation size={16} /> Get directions
          </span>
        </button>

        {chooser && (
          <div className="contact__map-chooser" onClick={() => setChooser(false)}>
            <div className="contact__map-chooser-card" role="dialog" onClick={(e) => e.stopPropagation()}>
              <p>Open navigation in</p>
              <div className="contact__map-apps">
                <a href={gmapsUrl} target="_blank" rel="noopener noreferrer" onClick={() => setChooser(false)}>
                  Google Maps
                </a>
                <a href={wazeUrl} target="_blank" rel="noopener noreferrer" onClick={() => setChooser(false)}>
                  Waze
                </a>
              </div>
              <button type="button" className="contact__map-cancel" onClick={() => setChooser(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}
